import { useState } from 'react';
import { yup } from 'shared/core/yup';
import { useApolloClient, useMutation } from '@apollo/client';

import { updateUser } from 'src/state/actions/users';
import { ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import useUser from 'src/hooks/use-user';
import { ExtendedUseTranslationResponse } from 'src/hooks/use-translation';
import { birthdayRegEx } from 'src/components/forms/form-utils';
import { CreateFraudAccountDocument } from 'src/gql/graphql';
import { CO } from 'src/components/forms/sign-up-form/medical-card-form';

type CompleteAccountFormFields = {
  firstName: string;
  lastName: string;
  phone: string;
  birthday: string;
  medicalCardNumber?: string;
  expirationDate?: string;
  state?: string;
};

const completeAccountValidation = (
  t: ExtendedUseTranslationResponse['t'],
  showMedicalCardForm?: boolean
): yup.AnyObjectSchema => {
  const medicalCardVerification = showMedicalCardForm
    ? {
        medicalCardNumber: yup.string().when([`state`], {
          is: (state: string) => state === CO,
          then: yup.string(),
          otherwise: yup.string().required(t('form.medicalCardNumber', 'medical card number is required')),
        }),
        expirationDate: yup
          .string()
          .beforeToday(`MM/DD/YYYY`, `medical card is expired`)
          .required(t('form.expirationDate', 'expiration date is required')),
        state: yup.string().required(t('form.state', 'state is required')),
      }
    : {};

  return yup.object().shape({
    firstName: yup.string().required(t('form.firstNameRequired', 'first name is required')),
    lastName: yup.string().required(t('form.lastNameRequired', 'last name is required')),
    phone: yup
      .string()
      .required(t('form.phoneNumberRequired', 'phone number is required'))
      .phone(t('form.validPhoneNumber', 'please enter a valid phone number')),
    birthday: yup
      .string()
      .required(t('form.birthdayRequired', 'birthday is required'))
      .isLegalAge(`MM/DD/YYYY`, t('form.isLegalAge', 'You must be at least 18 years old to use dutchie'))
      .matches(birthdayRegEx, t('form.validBirthday', 'please enter a valid birthday')),
    ...medicalCardVerification,
  });
};

type UseCompleteAccountProps = {
  close: () => void;
  showMedicalCardForm?: boolean;
};

type UseCompleteAccountReturn = {
  handleSubmit: (values: CompleteAccountFormFields) => Promise<void>;
  isLoading: boolean;
  completeAccountValidation: typeof completeAccountValidation;
};

export const useCompleteAccount = ({
  close,
  showMedicalCardForm,
}: UseCompleteAccountProps): UseCompleteAccountReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const User = useUser();
  const apolloClient = useApolloClient();
  const showErnie = useErnie();
  const [createFraudMutation] = useMutation(CreateFraudAccountDocument);

  const handleSubmit = async ({
    firstName,
    lastName,
    phone,
    birthday,
    medicalCardNumber,
    expirationDate,
    state,
  }: CompleteAccountFormFields): Promise<void> => {
    setIsLoading(true);

    const updatedProfile = {
      ...User.profile,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone,
      birthday,
      ...(showMedicalCardForm && {
        medicalCard: {
          number: state !== CO ? medicalCardNumber : '',
          expirationDate,
          state,
        },
      }),
    };

    const { success } = await updateUser(apolloClient, {
      profile: updatedProfile,
    });

    if (success) {
      // Call Payments service to fire create_account event in Sift once a new user has been created.
      // This call is async and does not affect the user experience, so we don't need to wait for it to complete.
      if (User.id) {
        createFraudMutation({
          variables: {
            payload: {
              userId: User.id,
              userEmail: User.email,
              name: `${String(firstName)} ${String(lastName)}`,
              phone,
              paymentMethods: [],
            },
          },
        }).catch((error) => {
          console.error('Error creating fraud account', error);
        });
      }

      User.refetch();
      showErnie('Account update successfully.', ERNIE_TYPES.SUCCESS);
      close();
    } else {
      showErnie('We were unable to save your information. Please try again.', ERNIE_TYPES.DANGER);
    }

    setIsLoading(false);
  };

  return {
    handleSubmit,
    isLoading,
    completeAccountValidation,
  };
};
